/* Deriving lesson status from completions.

   Pure logic, no React — so it can be reasoned about and tested on its own.
*/

/** Whether anything is held shut.

    **Off for v1 — no content is locked.** The gating machinery below is intact
    and tested; this is the single value that decides whether it holds anything
    back. With it off, `applyProgress` still marks what is done, what is active
    and what is next — the reading order still reads as an order — but nothing
    is ever `locked`, and a unit's `gated` flag is ignored.

    Turn it on by flipping this to `true`, or per call with
    `applyProgress(units, done, { locking: true })`. Nothing else has to change:
    every consumer already branches on `status`, so the UI states, the disabled
    buttons and the jump-menu filtering all come back on their own.
*/
export const LOCKING_ENABLED = false;

/** Derive every lesson's status from what the student has actually completed.

    Course content carries structure and order; it does not carry student state.
    A lesson is `done` if it is in storage, the first lesson that is not done is
    `active`, and the one after it is `next`.

    What happens to everything beyond depends on `LOCKING_ENABLED`:

    - **locking on** — `locked`, and a unit may set `gated: true` to hold itself
      shut regardless of progress ("unit 5 opens in week 8").
    - **locking off** (the v1 default) — `open`: available, not yet started, and
      not the one we are pointing at. `gated` is ignored.

    Everything else is computed, so a fresh student always starts at lesson one
    and nothing can drift out of step.
*/
export function applyProgress(units, done, { locking = LOCKING_ENABLED } = {}) {
  /* The status for "not done, and not one of the two we are pointing at". It is
     the only thing locking changes, which is what keeps this switch honest —
     there is no second code path to drift. */
  const REST = locking ? 'locked' : 'open';

  let seenOpen = 0;          // 0 = still looking for the active lesson
  let gateClosed = false;

  const out = units.map(u => {
    if (locking && u.gated) gateClosed = true;
    const lessons = u.lessons.map(l => {
      if (done[l.id]) return { ...l, status: 'done' };
      if (gateClosed) return { ...l, status: 'locked' };
      if (seenOpen === 0) { seenOpen = 1; return { ...l, status: 'active' }; }
      if (seenOpen === 1) { seenOpen = 2; return { ...l, status: 'next' }; }
      return { ...l, status: REST };
    });

    const allDone = lessons.every(l => l.status === 'done');
    const hasOpen = lessons.some(l => l.status === 'active');
    return { ...u, lessons, status: allDone ? 'done' : hasOpen ? 'active' : REST };
  });

  return out;
}
